import React, { useRef, useState } from 'react'
import { Button, DatePicker as AntDatePicker, Flex } from 'antd'
import { CloseOutlined, PlusOutlined } from '@ant-design/icons'
import dayjs, { Dayjs } from 'dayjs'
import { useTranslation } from 'react-i18next'
import { PeriodGroup } from '../../../types/periodGroup/PeriodGroup'
import { PeriodGroupSelection } from '../../../redux/context/filters/types'
import { customPeriodGroup } from '../../../utils/periodGroups/utils'
import PeriodTabFooter from './PeriodTabFooter'

type RangeEntry = { id: number; value: [Dayjs, Dayjs] | null }

interface CustomPeriodTabProps {
  initialSelection?: PeriodGroupSelection & { type: 'custom' }
  onSubmit: (periodGroups: PeriodGroup[], selection: PeriodGroupSelection) => void
  onClear: () => void
}

const CustomPeriodTab: React.FC<CustomPeriodTabProps> = ({ initialSelection, onSubmit, onClear }) => {
  const { t } = useTranslation()
  const rangeIdRef = useRef(0)

  const nextRangeId = () => {
    rangeIdRef.current += 1
    return rangeIdRef.current
  }

  const initialRanges: RangeEntry[] = initialSelection?.ranges?.map(r => ({
    id: nextRangeId(),
    value: [dayjs(r.startDate), dayjs(r.endDate)] as [Dayjs, Dayjs]
  })) || [{ id: nextRangeId(), value: null }]

  const [ranges, setRanges] = useState<RangeEntry[]>(initialRanges)

  const updateRange = (id: number, dates: [Dayjs, Dayjs] | null) => {
    setRanges(prev => prev.map(r => (r.id === id ? { ...r, value: dates } : r)))
  }

  const addRange = () => {
    setRanges(prev => [...prev, { id: nextRangeId(), value: null }])
  }

  const removeRange = (id: number) => {
    setRanges(prev => prev.filter(r => r.id !== id))
  }

  const handleSubmit = () => {
    const validRanges = ranges.filter((r): r is RangeEntry & { value: [Dayjs, Dayjs] } => r.value !== null)
    if (validRanges.length === 0) return
    const periodGroups = validRanges.map(({ value: [start, end] }) => customPeriodGroup(start, end))
    onSubmit(periodGroups, {
      type: 'custom',
      ranges: validRanges.map(({ value: [start, end] }) => ({
        startDate: start.format('YYYY-MM'),
        endDate: end.format('YYYY-MM')
      }))
    })
  }

  return (
    <div>
      <Flex vertical gap={8}>
        {ranges.map(entry => (
          <Flex key={entry.id} gap={4} align="center">
            <AntDatePicker.RangePicker
              style={{ flex: 1 }}
              placeholder={[t('periodSelect:start'), t('periodSelect:end')]}
              format="YYYY-MM"
              picker="month"
              allowClear
              value={entry.value}
              onChange={dates => updateRange(entry.id, dates as [Dayjs, Dayjs] | null)}
            />
            {ranges.length > 1 && (
              <Button type="text" size="small" icon={<CloseOutlined />} onClick={() => removeRange(entry.id)} />
            )}
          </Flex>
        ))}
        <Button type="dashed" size="small" icon={<PlusOutlined />} onClick={addRange}>
          {t('global:add')}
        </Button>
      </Flex>
      <PeriodTabFooter onSubmit={handleSubmit} onClear={onClear} />
    </div>
  )
}

export default CustomPeriodTab
